'use client'
import dynamic from 'next/dynamic'
import { createContext, useContext, useState } from 'react'
import { TermsUpdateAckGate } from '@domain/termsUpdateAck/TermsUpdateAckGate'

interface TermsAndConditionsContext {
  isTermsAndConditionsModalOpen: boolean
  setIsTermsAndConditionsModalOpen: (value: boolean) => void
  isPrivacyPolicyModalOpen: boolean
  setIsPrivacyPolicyModalOpen: (value: boolean) => void
  isWlthPolicyModalOpen: boolean
  setIsWlthPolicyModalOpen: (value: boolean) => void
}

export const CWDocumentsContext = createContext<TermsAndConditionsContext | undefined>(
  undefined,
)

const TermsAndConditionsDialog = dynamic(
  () =>
    import('./TermsAndConditionsDialog').then((m) => ({
      default: m.TermsAndConditionsDialog,
    })),
  { ssr: false },
)
const PrivacyPolicyDialog = dynamic(
  () =>
    import('@components/shared/privacy-policy').then((m) => ({
      default: m.PrivacyPolicyDialog,
    })),
  { ssr: false },
)
const WlthPolicyDialog = dynamic(
  () =>
    import('./WlthPolicyDialog').then((m) => ({
      default: m.WlthPolicyDialog,
    })),
  { ssr: false },
)

export const CWDocumentsProvider = ({ children }) => {
  const [isTermsAndConditionsModalOpen, setIsTermsAndConditionsModalOpen] =
    useState(false)
  const [isPrivacyPolicyModalOpen, setIsPrivacyPolicyModalOpen] = useState(false)
  const [isWlthPolicyModalOpen, setIsWlthPolicyModalOpen] = useState(false)

  const [termsEverOpened, setTermsEverOpened] = useState(false)
  const [privacyEverOpened, setPrivacyEverOpened] = useState(false)
  const [wlthPolicyEverOpened, setWlthPolicyEverOpened] = useState(false)

  const handleSetTerms = (value: boolean) => {
    if (value) setTermsEverOpened(true)
    setIsTermsAndConditionsModalOpen(value)
  }

  const handleSetPrivacy = (value: boolean) => {
    if (value) setPrivacyEverOpened(true)
    setIsPrivacyPolicyModalOpen(value)
  }

  const handleSetWlthPolicy = (value: boolean) => {
    if (value) setWlthPolicyEverOpened(true)
    setIsWlthPolicyModalOpen(value)
  }

  return (
    <CWDocumentsContext.Provider
      value={{
        isTermsAndConditionsModalOpen,
        setIsTermsAndConditionsModalOpen: handleSetTerms,
        isPrivacyPolicyModalOpen,
        setIsPrivacyPolicyModalOpen: handleSetPrivacy,
        isWlthPolicyModalOpen,
        setIsWlthPolicyModalOpen: handleSetWlthPolicy,
      }}
    >
      {children}
      {termsEverOpened && <TermsAndConditionsDialog />}
      {privacyEverOpened && <PrivacyPolicyDialog />}
      {wlthPolicyEverOpened && <WlthPolicyDialog />}
      <TermsUpdateAckGate />
    </CWDocumentsContext.Provider>
  )
}

const defaultCWDocuments: TermsAndConditionsContext = {
  isTermsAndConditionsModalOpen: false,
  setIsTermsAndConditionsModalOpen: () => {},
  isPrivacyPolicyModalOpen: false,
  setIsPrivacyPolicyModalOpen: () => {},
  isWlthPolicyModalOpen: false,
  setIsWlthPolicyModalOpen: () => {},
}

export const useCWDocumentsContext = () => {
  const ctx = useContext(CWDocumentsContext)
  if (!ctx) {
    return defaultCWDocuments
  }

  return ctx
}
